/**
 * iOS Safariで動画を確実に自動再生するためのクラス
 * ユーザーインタラクション後に動画を自動再生し、以降の動画も自動再生できるようにする
 * 
 * @example
 * const autoPlayer = new VideoAutoPlayer();
 * autoPlayer.init();
 * 
 * // 動画要素に data-autoplay="true" を追加すると自動管理される
 * // <video data-autoplay="true" muted playsinline src="video.mp4"></video>
 */
class VideoAutoPlayer {
    constructor() {
        this.isUnlocked = false;
        this.pendingVideos = [];
        this.observer = null;
    }

    /**
     * 初期化処理
     * ページ読み込み時とユーザーインタラクション検知の設定
     */
    init() {
        // ページ読み込み完了後に実行
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupAutoplay());
        } else {
            this.setupAutoplay();
        }
    }

    /**
     * 自動再生のセットアップ
     */
    setupAutoplay() {
        this.setupIntersectionObserver();
        this.findAndSetupVideos();
        this.setupUserInteractionUnlock();
    }

    /**
     * 全ての動画要素を見つけてセットアップ
     */
    findAndSetupVideos() {
        const videos = document.querySelectorAll('video[data-autoplay="true"]');
        videos.forEach(video => this.setupVideo(video));
    }

    /**
     * 個別の動画要素をセットアップ
     * @param {HTMLVideoElement} video - 設定する動画要素
     */
    setupVideo(video) {
        // iOS Safari対応の基本設定
        video.muted = true; // 音声をミュート（重要！）
        video.playsInline = true; // インライン再生
        video.preload = 'auto'; // 事前読み込み

        // 既に画面内にある場合は即座に再生試行
        if (this.isInViewport(video)) {
            this.tryPlayVideo(video);
        }

        // 画面内に入った時の処理用に配列に追加
        this.pendingVideos.push(video);
    }

    /**
     * Intersection Observerのセットアップ
     * 動画が画面内に入った時に自動再生を試行
     */
    setupIntersectionObserver() {
        const options = {
            root: null,
            rootMargin: '50px',
            threshold: 0.1
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const video = entry.target;
                    this.tryPlayVideo(video);
                }
            });
        }, options);

        // 既存の動画を監視対象に追加
        document.querySelectorAll('video[data-autoplay="true"]').forEach(video => {
            this.observer.observe(video);
        });
    }

    /**
     * ユーザーインタラクション後の自動再生解除
     */
    setupUserInteractionUnlock() {
        const unlockEvents = ['touchstart', 'touchend', 'mousedown', 'keydown', 'click'];

        const unlock = () => {
            this.isUnlocked = true;

            // 待機中の動画を全て再生
            this.pendingVideos.forEach(video => {
                if (this.isInViewport(video)) {
                    this.tryPlayVideo(video);
                }
            });

            // イベントリスナーを削除
            unlockEvents.forEach(event => {
                document.removeEventListener(event, unlock, true);
            });
        };

        unlockEvents.forEach(event => {
            document.addEventListener(event, unlock, true);
        });
    }

    /**
     * 動画の再生を試行
     * @param {HTMLVideoElement} video - 再生する動画要素
     */
    async tryPlayVideo(video) {
        try {
            await video.play();
            // console.log('動画再生成功:', video.src || video.currentSrc);

            // 再生成功した動画は監視から除外
            if (this.observer) {
                this.observer.unobserve(video);
            }
        } catch (error) {
            console.log('動画再生失敗:', error.message);

            // まだユーザーインタラクションが発生していない場合
            if (!this.isUnlocked) {
                console.log('ユーザーインタラクション待機中...');
            }
        }
    }

    /**
     * 要素が画面内にあるかチェック
     * @param {HTMLElement} element - チェックする要素
     * @returns {boolean} 画面内にあるかどうか
     */
    isInViewport(element) {
        const rect = element.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    /**
     * 新しい動画要素を動的に追加
     * @param {HTMLVideoElement} video - 追加する動画要素
     */
    addVideo(video) {
        this.setupVideo(video);
        if (this.observer) {
            this.observer.observe(video);
        }
    }

    /**
     * 全ての動画を強制再生（デバッグ用）
     */
    playAllVideos() {
        const videos = document.querySelectorAll('video[data-autoplay="true"]');
        videos.forEach(video => this.tryPlayVideo(video));
    }
}

// 自動初期化（グローバルで使用する場合）
const videoAutoPlayer = new VideoAutoPlayer();
videoAutoPlayer.init();

// グローバルに公開（必要に応じて）
// window.VideoAutoPlayer = VideoAutoPlayer;
// window.videoAutoPlayer = videoAutoPlayer;